// src/components/FittingRoomPanel.jsx
import React, { useMemo, useState } from "react";
import { logTryOnStarted } from "../lib/interactionEventsApi";
import { optimizedImageUrl } from "../lib/optimizedImageUrl";

function getCatalogImage(item) {
  const gallery = Array.isArray(item?.images)
    ? item.images
        .map((img) => (typeof img === "string" ? img : img?.url))
        .filter(Boolean)
    : [];

  return item?.image_url || gallery[0] || item?.catalogImage || null;
}

export default function FittingRoomPanel({
  items,
  onClose,
  onRemove,
  onTryOn,
  onAddToEdit, // optional
  tryOnLoadingId, // NEW
}) {

    const [infoItem, setInfoItem] = useState(null);

  const REASON_COPY = useMemo(
    () => ({
      POSE_ANGLE: "The store photo is angled/posed.",
      SHADOW_CONTAMINATION: "Strong shadows were reduced.",
      EDGE_NOISE: "Edges were cleaned to reduce noise.",
      NECKLINE_OVERREACH: "Neckline was corrected.",
      MULTI_ITEM_SUSPECTED: "More than one item may be present.",
      LOW_CONFIDENCE: "The cutout confidence was low.",
      MISSING_WAIST: "Waistline wasn't captured (bottoms).",
TOO_SHORT_FOR_BOTTOMS: "The cutout is too short for bottoms.",
TINY_OR_SHARDS: "Only small fragments were detected.",
FRAGMENTED_GARMENT: "The cutout is fragmented into multiple pieces.",
LIKELY_DRESS_OR_MULTI_ITEM: "This looks like a full-body shot or multiple items.",
MISSING_SHOULDERS: "Upper structure (shoulders/neckline) may be missing.",
LOW_CONFIDENCE_OUTER_GEOMETRY: "Outerwear cutout confidence was low.",
LOW_CONFIDENCE_TOPS_GEOMETRY: "Top cutout confidence was low.",
LOW_CONFIDENCE_BOTTOMS_GEOMETRY: "Bottom cutout confidence was low.",
UNKNOWN_CATEGORY_DEFAULT_CLEANUP: "This category uses default cleanup rules.",
EMPTY_MASK: "No garment pixels detected.",
    }),
    []
  );

  function getQuality(item) {
  return (
    item?.tryonQuality ?? {
      fidelityLevel: "UNKNOWN",
      qualityScore: null, // null until backend sends it
      reasons: [],
      sourceImageUrl: item?.image,
    }
  );
}
function QualityBadge({ item }) {
  const q = getQuality(item);
  const score = q?.qualityScore;

  if (score == null) {
    return (
      <span style={{ fontSize: 12, opacity: 0.7 }}>
        Quality: —
      </span>
    );
  }

  const label = score >= 80 ? "OK" : score >= 60 ? "Maybe" : "Low";
  const emoji = score >= 80 ? "🟢" : score >= 60 ? "🟡" : "🔴";

  return (
    <span style={{ fontSize: 12 }}>
      {emoji} Quality: {label} ({Math.round(score)})
    </span>
  );
}

 
  function fidelityLabel(level) {
    if (level === "ENHANCED") return "Enhanced for try-on";
    if (level === "APPROX") return "Approximation";
    return null;
  }

  function confidenceLabel(score) {
    if (score >= 80) return "High";
    if (score >= 60) return "Medium";
    return "Low";
  }

function getTryOnAvailability(item) {
  const tryOn = item?.try_on || item?.tryOn || null;
  const assets = tryOn?.assets || {};

  const status = tryOn?.status || null;
  const hasFront = !!assets.front;
  const hasBack = !!assets.back;

  // 🔴 NEW: mesh gating
  const hasMesh = !!item.meshUrl;

  const isFrontOnly = status === "front_only" && hasFront && !hasBack;
  const isFull = status === "available" && hasFront && hasBack;

  const isUnavailable =
    !hasMesh ||          // 🔴 CRITICAL
    status === "unavailable" ||
    !hasFront;

  return {
    status,
    hasFront,
    hasBack,
    isFrontOnly,
    isFull,
    isUnavailable,
  };
}

function getAvailabilityMessage(item) {
  const availability = getTryOnAvailability(item);

  if (availability.isUnavailable) {
    return "This product is not available for try-on.";
  }

  if (availability.isFrontOnly) {
    return "Back view not available for this product.";
  }

  return null;
}

  function getTryOnButtonLabel(item, isLoading) {
    if (isLoading) return "Loading…";

    const availability = getTryOnAvailability(item);

    if (availability.isFrontOnly) return "Try on (front only)";

    return "Try on";
  }

    if (!items) return null;


  return (
    <div className="side-panel right-panel">
      <div className="side-panel-header">
        <h2>
  Fitting Room{" "}
  <span style={{ fontSize: 12, opacity: 0.75, fontWeight: 500 }}>
    ({items?.length || 0})
  </span>
</h2>

        <button className="panel-close" onClick={onClose}>
          ✕
        </button>
      </div>

      <div className="side-panel-body">
        <div
  style={{
    fontSize: 12,
    lineHeight: 1.4,
    color: "rgba(43, 43, 43, 0.7)",
    background: "rgba(251, 247, 242, 0.7)",
    border: "1px solid rgba(43, 43, 43, 0.08)",
    borderRadius: 10,
    padding: "8px 10px",
    marginBottom: 10,
  }}
>
  Try-on is for visualisation only — fit, fabric and proportions may vary in real life.
</div>
                {items.length === 0 ? (
          <p className="field-help" style={{ marginTop: 0 }}>
            Your fitting room is empty. Browse boutiques and add pieces here to try on.
          </p>
        ) : (
          <div className="fitting-grid" style={{ gridTemplateColumns: "1fr" }}>
            {items.map((item) => {
              const availabilityMessage = getAvailabilityMessage(item);
              const availability = getTryOnAvailability(item);
              const isUnavailable = availability.isUnavailable;

              return (
              <div
                key={item.id}
                className="FittingRoomPanel-item fitting-item"
                onClick={() => {
                  if (tryOnLoadingId === item.id) return;

                  const availability = getTryOnAvailability(item);
                  if (availability.isUnavailable) return;

                  logTryOnStarted({
  productId: item.id,
  surface: "fitting_room",
  category: item.category,
  variant: item.variant || null,
});

onTryOn(item);
                }}

                style={{
                  textAlign: "left",
                  padding: 10,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <button
                  className="remove-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(item.id);
                  }}
                  aria-label="Remove item"
                  title="Remove"
                >
                  ✕
                </button>

                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <img
  className="fitting-thumb"
  src={optimizedImageUrl(getCatalogImage(item), { width: 160, quality: 72 })}
  loading="lazy"
  decoding="async"
                    alt={item.name}
                    style={{
                      width: 64,
                      height: 64,
                      objectFit: "cover",
                      borderRadius: 12,
                      border: "1px solid rgba(43, 43, 43, 0.08)",
                      background: "rgba(251, 247, 242, 0.6)",
                    }}
                  />

                  <div style={{ flex: 1, minWidth: 0 }}>
  <div
    className="fitting-name"
    style={{
      marginTop: 0,
      fontWeight: 600,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
  >
    {item.name}
  </div>

  {/* --- Try-on quality pill (only show for Enhanced/Approx) --- */}
  {(() => {
    const q = getQuality(item);
    const label = fidelityLabel(q.fidelityLevel);
    if (!label) return null;

    const isApprox = q.fidelityLevel === "APPROX";

    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation(); // don't trigger try-on
          setInfoItem(item);
        }}
        title="About this try-on cutout"
        style={{
          alignSelf: "flex-start",
          marginTop: 6,
          marginBottom: 2,
          padding: "4px 8px",
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 650,
          letterSpacing: 0.1,
          border: "1px solid rgba(43, 43, 43, 0.12)",
          background: isApprox ? "rgba(176, 140, 108, 0.10)" : "rgba(251, 247, 242, 0.85)",
          color: "#5b1f2b",
          cursor: "pointer",
        }}
      >
        {label}
      </button>
    );
  })()}

  <div
    className="fitting-category"
    style={{ opacity: 0.75, textTransform: "capitalize" }}
  >
    {item.brand ? `${item.brand} • ` : ""}
    {item.category}
  </div>

  {availabilityMessage ? (
    <div
      style={{
        marginTop: 4,
        fontSize: 12,
        color: "rgba(43, 43, 43, 0.68)",
        lineHeight: 1.35,
      }}
    >
      {availabilityMessage}
    </div>
  ) : null}
</div>

                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    className="gold-btn"
                    style={{
                      flex: 1,
                      padding: "10px 12px",
                      borderRadius: 12,
                      opacity: tryOnLoadingId === item.id || isUnavailable ? 0.5 : 1,
                    }}
                    disabled={tryOnLoadingId === item.id || isUnavailable}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (tryOnLoadingId === item.id || isUnavailable) return;
                      logTryOnStarted({
  productId: item.id,
  surface: "fitting_room",
  category: item.category,
  variant: item.variant || null,
});

onTryOn(item);
                    }}
                  >
  {getTryOnButtonLabel(item, tryOnLoadingId === item.id)}
</button>


                  <button
                    className="ghost-btn"
                    style={{ padding: "10px 12px", borderRadius: 12 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddToEdit?.(item);
                    }}
                    title="Pin to edit (coming soon)"
                  >
                    Add to Edit
                  </button>
                </div>
              </div>
              );
            })}
          </div>
               )}
      </div>

      {/* --- Try-on quality modal --- */}

      {infoItem && (() => {
        const q = getQuality(infoItem);
        const label = fidelityLabel(q.fidelityLevel) ?? "Cleaned";
        const score = typeof q.qualityScore === "number" ? q.qualityScore : 0;
        const conf = confidenceLabel(score);
        const reasons = Array.isArray(q.reasons) ? q.reasons : [];
        const sourceUrl = q.sourceImageUrl || getCatalogImage(infoItem);

        return (
          <div
            onClick={() => setInfoItem(null)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(43, 43, 43, 0.35)",
              zIndex: 9999,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              padding: 16,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "min(520px, 100%)",
                borderRadius: 18,
                background: "#fbf7f2",
                border: "1px solid rgba(43, 43, 43, 0.10)",
                boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
                padding: 14,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: "#2b2b2b" }}>Try-on image</div>
                  <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>
                    Type: <span style={{ fontWeight: 650 }}>{label}</span> • Confidence:{" "}
                    <span style={{ fontWeight: 650 }}>{conf}</span>
                    <span style={{ opacity: 0.7 }}> ({Math.round(score)}/100)</span>
                  </div>
                </div>

                <button
                  className="panel-close"
                  onClick={() => setInfoItem(null)}
                  aria-label="Close"
                  style={{
                    borderRadius: 12,
                    width: 34,
                    height: 34,
                    border: "1px solid rgba(43, 43, 43, 0.12)",
                    background: "rgba(251, 247, 242, 0.9)",
                    cursor: "pointer",
                  }}
                >
                  ✕
                </button>
              </div>

              {reasons.length > 0 ? (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#2b2b2b", marginBottom: 6 }}>
                    Why this label
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.45, color: "#2b2b2b" }}>
                    {reasons.map((r) => (
                      <li key={r} style={{ marginBottom: 4, opacity: 0.92 }}>
                        {REASON_COPY[r] ?? r}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div style={{ marginTop: 12, fontSize: 12, opacity: 0.85 }}>
                  This cutout was produced directly from the store image.
                </div>
              )}

              <div style={{ display: "flex", gap: 10, marginTop: 14, alignItems: "center" }}>
                {sourceUrl ? (
                  <a
                    href={sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="ghost-btn"
                    style={{
                      padding: "10px 12px",
                      borderRadius: 12,
                      textDecoration: "none",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    View original photo
                  </a>
                ) : null}

                <button
                  className="gold-btn"
                  style={{ marginLeft: "auto", padding: "10px 12px", borderRadius: 12 }}
                  onClick={() => setInfoItem(null)}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>

  );
}

